import React, { useEffect, useState, useRef } from 'react';
import { Radio, Space, Tabs, List, Button, Tag, Modal, Typography,
  Badge, Avatar, Collapse, Row, Col, Alert, PageHeader, Spin,
  message as antdMessage,
 } from 'antd';
import {
  EditFilled,
  EditOutlined,
  EnableOutlined,
  AppstoreAddOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  CheckCircleTwoTone,
  LinkOutlined,
  LayoutTwoTone,
  SettingTwoTone,
  CheckSquareTwoTone,
  AppstoreTwoTone,
  ShoppingCartOutlined,
  CalculatorOutlined,
  CalendarOutlined,
  DollarCircleOutlined,
  PieChartOutlined,
  AuditOutlined,
  ContactsOutlined,
  StarOutlined,
  PercentageOutlined,
  DollarOutlined,
  GlobalOutlined,
} from '@ant-design/icons';

import '../../App.scss';
import 'antd/dist/antd.css';
import { colorForBackgroundColor, getContrastWithRGBColor } from '../../utils/RGBColorToHex';
import ProductSwitcher, { capitalize } from '../../utils/ProductSwitcher';
import { useTagDetails } from '../../models/useTagDetails';
import { useSitesByType } from '../../models/useSites';
import { useTeamSitesByType } from '../../models/useTeamSites';
import ContrastColor from '../customer/ContrastColor';
import TrackerService from '../../utils/TrackerService';

import SuccessPagePreview from './SuccessPagePreview';
import ThankYouEmailPreview from './ThankYouEmailPreview';
import TipPagePreview from './TipPagePreview';
import TeamPagePreview from './TeamPagePreview';
import { TeamTapBehaviourUpdate } from './TeamTapBehaviourControl';
import { TeamEditColorUpdate } from './ColorPickerControl';
import { TeamImageEditor } from './UploadableImageEditor';
import { TeamDefaultMemberSettingsUpdate } from './TeamMembershipSettings';
import { EditTeamNameSettingsUpdate } from './TeamNameSettings';
import { TeamPaymentDetailsSettingsUpdate } from './TeamPaymentDetailsSettings';
import TeamPayAndTipSettings from './TeamPayAndTipSettings';
import { EditTeamReviewSettingsUpdate, EditTeamReviewForm } from './TeamReviewsSettings';
import TeamEditorValues from './TeamEditorValues';
import TeamEditorPercents from './TeamEditorPercents';
import { useTeamDetails } from './useTeamDetails';
import { TeamLocationContactSettingsUpdate } from './TeamLocationContactSettings';

const { Panel } = Collapse;
const { TabPane } = Tabs;
const { Text } = Typography;

const ExternalLink = ({ url }) => {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="link-primary text-decoration-underline">
      {url}
    </a>
  );
}

const TabTeamSettings = ({ teamId, team, onLoading }) => {
  const { topLogo, rgbColor } = team || {};

  const handleLoading = ({ message }) => {
    onLoading && onLoading({ message });
  }

  return (
    <div>
      <div className='h3 mt-4'>Banner settings</div>
      <div className='container mt-4'>
        <TeamImageEditor
          teamId={teamId}
          currentUrl={topLogo}
          isLoggedInMemberAdmin={true}
          onUpdateImage={(imageUrl) => console.log('updated image', imageUrl)}
          onLoading={handleLoading}
        />
        <div className='text'>
          Click on image to change
        </div>
      </div>

      <div className='container mt-4'>
        <TeamEditColorUpdate
          teamId={teamId}
          team={team}
          rgbColor={rgbColor}
          onLoading={handleLoading}
        />
      </div>

      <div className='container mt-4'>
        <EditTeamNameSettingsUpdate
          teamId={teamId} team={team}
          onLoading={handleLoading}
        />
      </div>

    </div>
  ) 
}

const TabTeamSettingsImage = ({ teamId, team, onLoading }) => {
  const { topLogo, rgbColor } = team || {};

  const handleLoading = ({ message }) => {
    onLoading && onLoading({ message });
  }

  return (
    <div>
      <div className='h3 mt-4'>Banner image</div>
      <div className='container mt-4'>
        <TeamImageEditor
          teamId={teamId}
          currentUrl={topLogo}
          isLoggedInMemberAdmin={true}
          onUpdateImage={(imageUrl) => console.log('updated image', imageUrl)}
          onLoading={handleLoading}
        />
        <div className='text'>
          Click on image to edit
        </div>
      </div>

      <div className='h3 mt-4'>Banner color settings</div>
      <div className='container mt-4'>
        <TeamEditColorUpdate
          teamId={teamId}
          team={team}
          rgbColor={rgbColor}
          onLoading={handleLoading}
        />
      </div>
      <div className='text'>
        Click on a color to change
      </div>
    </div>
  )
}

const TabTeamSettingsColor = ({ teamId, team, onLoading }) => {
  const { topLogo, rgbColor } = team || {};

  const handleLoading = ({ message }) => {
    onLoading && onLoading({ message });
  }

  return (
    <div>
      <div className='h3 mt-4'>Banner color settings</div>
      <div className='container mt-4'>
        <TeamEditColorUpdate
          teamId={teamId}
          team={team}
          rgbColor={rgbColor}
          onLoading={handleLoading}
        />
      </div>
    </div>
  ) 
}

const TabTeamSettingsName = ({ teamId, team, onLoading }) => {
  const { topLogo, rgbColor } = team || {};

  const handleLoading = ({ message }) => {
    onLoading && onLoading({ message });
  }

  return (
    <div>
      <div className='h3 mt-4'>Team name settings</div>
      <div className='container mt-4'>
        <EditTeamNameSettingsUpdate
          teamId={teamId} team={team}
          onLoading={handleLoading}
        />
      </div>
    </div>
  ) 
}

const TabTeamLocationContact = ({ teamId, team, onLoading, changedByClientId }) => {
  const handleLoading = ({ message }) => {
    onLoading && onLoading({ message });
  };

  return (
    <div>
      <div className="h3 mt-4">Location</div>
      <div className="container mt-4">
        <TeamLocationContactSettingsUpdate
          teamId={teamId}
          team={team}
          clientId={changedByClientId}
          onLoading={handleLoading}
        />
      </div>
    </div>
  );
};

const TabPaymentDetailsSettings = ({ teamId, team, onLoading }) => {
  const { paymentDetails } = team || {};

  return (
    <div>
      <div className='container mt-4'>
        <TeamPaymentDetailsSettingsUpdate
          teamId={teamId}
          team={team}
          onLoading={onLoading}
        />
      </div>
    </div>
  )
}

const TabPayAndTipSettings = ({ teamId, team, onLoading, changedByClientId, isAdmin }) => {
  return (
    <div className='container mt-4'>
      <TeamPayAndTipSettings
        teamId={teamId}
        team={team}
        onLoading={onLoading}
        changedByClientId={changedByClientId}
        isAdmin={isAdmin}
      />
    </div>
  )
}

const TabMemberSettings = ({ teamId, team, onLoading }) => {
  const { teamTapBehaviour, displayPermissions } = team || {};

  return (
    <div>
      <div className='row justify-content-center mt-4'>
        <div className='col-auto'>
          {team && teamTapBehaviour &&
            <TeamTapBehaviourUpdate
              teamId={teamId}
              team={team}
              teamTapBehaviour={teamTapBehaviour}
              displayPermissions={displayPermissions}
              onLoading={onLoading}
            />
          }
        </div>
      </div>

      <div className='container mt-4'>
        <TeamDefaultMemberSettingsUpdate
          teamId={teamId}
          team={team}
          onLoading={onLoading}
        />
      </div>
    </div>
  )
}

const AvatarWithCheckbox = ({ imageUrl, showCheckbox = true, isApp }) => {
  const BadgeIcon = () => {
    return <CheckCircleFilled style={{ backgroundColor: 'white', color: 'green', borderRadius: '50%' }} />
  };
  return (
    <Badge count={showCheckbox ? '✔︎' : null} status={showCheckbox ? 'success' : null} offset={[-36, 0]}>
      <Avatar src={imageUrl} size='large' shape='square' style={{ borderRadius: isApp ? '10%' : '4px' }}/>
    </Badge>
  );
};


const TeamSitesSelectionItem = ({ site, onLoading, onSelected, isAdmin }) => {
  const { brand, displayName, description, imageUrl, link, rgbColor, isApp: teamIsApp, type: siteType, templateButton, templateLink, templateColor, templateIsApp } = site || {};
  const { url, button, title, status } = site || {}
  const { id, teamId: tid, siteId, isRecent } = site || {}
  const isActive = !!tid;

  const isApp = templateIsApp !== undefined ? templateIsApp : !!teamIsApp;

  const onClickEdit = () => {
    onSelected && onSelected(site);
  }

  const titleName = isApp ? (description || displayName) : (title || displayName || 'Custom Link');
  const linkUrl = isApp ? (templateLink || link) : (url || link || null);
  const descriptionText = isApp ? null : (url ? null : description);
  const buttonLabel = isApp ? (templateButton || 'Download') : (button || templateButton || (siteType === 'social' ? 'Follow' : null));
  const brandColor = templateColor || rgbColor || '#ff0098';
  const targetUrl = url;

  const bgItem = isRecent ? '#77777711' : null;

  const actions = [];

  if (isActive) {
    actions.push(<Button key={`li-btn-actv`} icon={isRecent ? <EditFilled /> : <EditOutlined />} onClick={() => onClickEdit()}>{`Edit`}</Button>);
  }

  if (!isActive) {
    actions.push(<Button key={`li-btn-actv`} icon={<AppstoreAddOutlined />} onClick={() => onClickEdit()}>{`Activate`}</Button>);
  }

  const RowFooterLineItem = ({ imageUrl, displayName, isApp, brandColor }) => {
    return (
      <div className="d-flex align-items-center pt-1 px-0">
        <div className="me-2">
          <Avatar src={imageUrl} size={16} shape="square" style={{ borderRadius: isApp ? '10%' : '4px' }} />
        </div>
        <div className='pt-1 px-1'>
          <Text type="secondary" style={{ lineHeight: "24px", display: "inline-flex", alignItems: "center" }}>
            {displayName}
            {isApp && <Tag color="blue" className="mx-2" style={{ fontSize: '10px', lineHeight: '16px' }}>App</Tag>}
          </Text>
        </div>
      </div>
    );
  };

  return (
    <List.Item
      key={`li-site-${id}`}
      actions={actions}
      style={{ backgroundColor: bgItem, overflow: 'visible' }}
    >
      <List.Item.Meta
        title={
          <div className='h3' style={{ marginTop: '-12px', display: 'flex', alignItems: 'center', overflow: 'visible' }}>
            {titleName}
          </div>
        }
        description={
          <ul className='mx-1 align-items-center' style={{ listStyle: 'none', padding: 0, overflow: 'visible' }}>
            {descriptionText && <li>{descriptionText}</li>}
            {buttonLabel && <li className='py-2'>
              <Button
                style={{ backgroundColor: brandColor, borderColor: brandColor }}
                href={linkUrl}
                target="_blank"
              >
                <ContrastColor backgroundColor={brandColor}>
                  {buttonLabel}
                </ContrastColor>
              </Button>
            </li>}
            {!url && link && <li><ExternalLink url={link} /></li>}
            {displayName && (url || isApp) && <li><RowFooterLineItem imageUrl={imageUrl} displayName={displayName} isApp={isApp} brandColor={brandColor}/></li>}
          </ul>
        }
        avatar={<AvatarWithCheckbox imageUrl={imageUrl} showCheckbox={isActive && url} isApp={isApp} />}
      />
    </List.Item>
  );
}

const TeamSitesSelection = ({ teamId, team, onLoading, type, isAdmin, changedByClientId }) => {
  const filterByType = type || 'review';
  const checkForExistingBrand = filterByType === 'review' ? 'success' : null;
  const { reviewDetails } = team || {};
  const { success } = reviewDetails || {};
  const { url, title, button } = success || {};
  const [allSitesByType] = useSitesByType({ status: 'live', type: filterByType });
  const [teamSitesByType, createTeamSite, updateTeamSite, deactivateTeamSite] = useTeamSitesByType({ teamId, type: filterByType });
  const [isEditModal, setIsEditModal] = useState(false);
  const [selectedTeamSite, setSelectedTeamSite] = useState(null);
  const [recentTeamSite, setRecentTeamSite] = useState(null);
  const [isSuccessReviewCreated, setIsSuccessReviewCreated] = useState(false);
  const editFormCloseRef = useRef(null);
  let reviews = null;

  const migrateTeamReview = async (defaultSite) => {
    const { id: siteId, brand, type, rgbColor, isApp } = defaultSite || {};
    if (success && brand && siteId && type) {
      const successReviewDetails = {
        brand,
        siteId,
        teamId,
        type,
        status: url ? 'live' : 'created',
        url: url || null,
        title: title || null,
        button: button || null,
        rgbColor: rgbColor || null,
        isApp: !!isApp,
        ...(changedByClientId && { updatedByClientId: changedByClientId }),
      };
      setIsSuccessReviewCreated(true);
      onLoading({ message: `Migrating team review` });
      await createTeamSite(successReviewDetails);
      const tracker = new TrackerService({});
      tracker.mixpanelTrack('team_site_created', {
        teamId,
        teamName: team?.teamName,
        teamOwnerClientId: team?.teamOwnerClientId,
        siteId: siteId,
        brand,
        type,
        status: url ? 'live' : 'created',
        ...(changedByClientId && { changedByClientId }),
      });
      if (url) {
        tracker.mixpanelTrack('team_site_activated', {
          teamId,
          teamName: team?.teamName,
          teamOwnerClientId: team?.teamOwnerClientId,
          siteId: siteId,
          brand,
          type,
          status: 'live',
          url,
          ...(changedByClientId && { changedByClientId }),
        });
      }
      onLoading({});
    }
  }

  useEffect(() => {
    if (isSuccessReviewCreated) {
      return;
    }

    if (!checkForExistingBrand) {
      setIsSuccessReviewCreated(true);
      return;
    }

    if (team && !success) {
      setIsSuccessReviewCreated(true);
      return;
    }

    if (checkForExistingBrand && team && teamSitesByType && allSitesByType) {
      const teamSuccessReview = teamSitesByType.filter(({ brand }) => brand === checkForExistingBrand)[0];
      const defaultReviewSite = allSitesByType.filter(({ brand }) => brand === checkForExistingBrand)[0];

      if (defaultReviewSite && !teamSuccessReview) {
        migrateTeamReview(defaultReviewSite);
      }
    }
    
  }, [teamId, allSitesByType, teamSitesByType]);

  if (teamSitesByType && allSitesByType) {
    reviews = allSitesByType.map((site) => {
      const { id, brand } = site;
      let teamReview = teamSitesByType.filter(({ siteId, status }) => id === siteId && status !== 'deactivated')[0];
      const { teamId: tid } = teamReview || {};
      const { teamId: recentTid } = recentTeamSite || {};
      const isRecent = tid && recentTid === tid;
      return { 
        ...site, 
        ...teamReview, 
        teamId: tid, 
        isRecent, 
        siteId: id,
        templateButton: site.button,
        templateLink: site.link,
        templateColor: site.rgbColor,
        templateIsApp: !!site.isApp,
      }; 
    });
  }

  const onUpdated = async (teamSite) => {
    const { id, brand, url, title, button, rgbColor, campaign } = teamSite;
    const status = url ? 'live' : 'created';
    const wasLive = teamSite.status === 'live';
    await updateTeamSite(id, {
      brand,
      url,
      title,
      button,
      status,
      rgbColor,
      teamId,
      campaign,
      ...(changedByClientId && { updatedByClientId: changedByClientId }),
    });
    const tracker = new TrackerService({});
    tracker.mixpanelTrack('team_site_updated', {
      teamId,
      teamName: team?.teamName,
      teamOwnerClientId: team?.teamOwnerClientId,
      siteId: teamSite.siteId || id,
      brand,
      type: teamSite.type || filterByType,
      status,
      url,
      title,
      button,
      ...(changedByClientId && { changedByClientId }),
    });
    if (status === 'live' && !wasLive) {
      tracker.mixpanelTrack('team_site_activated', {
        teamId,
        teamName: team?.teamName,
        teamOwnerClientId: team?.teamOwnerClientId,
        siteId: teamSite.siteId || id,
        brand,
        type: teamSite.type || filterByType,
        status: 'live',
        url,
        ...(changedByClientId && { changedByClientId }),
      });
    }
    setIsEditModal(false);
    setSelectedTeamSite(null);
    setRecentTeamSite({ ...teamSite, status });
  };

  const onDeactivate = async (teamSite) => {
    const { id, brand, siteId, type } = teamSite;
    if (!id) return;
    onLoading({ message: 'Deactivating site...' });
    await deactivateTeamSite(id, changedByClientId);
    const tracker = new TrackerService({});
    tracker.mixpanelTrack('team_site_deactivated', {
      teamId,
      teamName: team?.teamName,
      teamOwnerClientId: team?.teamOwnerClientId,
      siteId: siteId || id,
      brand,
      type,
      ...(changedByClientId && { changedByClientId }),
    });
    onLoading({});
    setIsEditModal(false);
    setSelectedTeamSite(null);
  };

  const onSelected = async (site) => {
    const { teamId: tid, brand, url, button, title, rgbColor, siteId, templateColor, templateIsApp } = site || {};

    if (!site || !brand) {
      return;
    }

    if (tid) {
      setSelectedTeamSite({ ...site });
    } else {
      const status = 'created';
      const aNewTeamSite = {
        brand,
        url: url || null,
        button: button || null,
        title: title || null,
        status,
        rgbColor: rgbColor || templateColor || null,
        isApp: templateIsApp !== undefined ? templateIsApp : !!site.isApp,
        siteId,
        teamId,
        ...(changedByClientId && { updatedByClientId: changedByClientId }),
      };
      const ref = await createTeamSite(aNewTeamSite);
      const id = ref.id;
      const tracker = new TrackerService({});
      tracker.mixpanelTrack('team_site_created', {
        teamId,
        teamName: team?.teamName,
        teamOwnerClientId: team?.teamOwnerClientId,
        siteId,
        brand,
        type: aNewTeamSite.type || filterByType,
        status: 'created',
        ...(changedByClientId && { changedByClientId }),
      });
      setSelectedTeamSite({ ...site, ...aNewTeamSite, id });
    }
    setIsEditModal(true);
  }

  const onClickModalCancel = () => {
    const canClose = editFormCloseRef.current?.canClose?.();
    if (canClose === false) {
      antdMessage.warning('Please fill the required Button link before closing.');
      return;
    }
    setIsEditModal(false);
    setSelectedTeamSite(null);
  };

  return (
    <>
      {reviews &&
        <List
          size="large"
          itemLayout="horizontal"
          pagination={{
            pageSize: 8,
          }}
          style={{ textAlign: 'left', overflow: 'visible' }}
          className="team-sites-list"
          dataSource={reviews}
          renderItem={site => (
            <TeamSitesSelectionItem
              key={`site-${site.id}`}
              site={site}
              onLoading={onLoading}
              onSelected={onSelected}
              isAdmin={isAdmin}
            />
          )}
        />
      }
      {
        <Modal
          title={(selectedTeamSite && selectedTeamSite.displayName) ? selectedTeamSite.displayName : 'Edit Review'}
          visible={isEditModal}
          onCancel={() => onClickModalCancel()}
          footer={null}
          bodyStyle={{ minHeight: '200px', minWidth: '280px' }}
          destroyOnClose={true}
        >
          <EditTeamReviewForm
            team={team}
            teamId={teamId}
            review={selectedTeamSite}
            onUpdated={onUpdated}
            onDeactivate={onDeactivate}
            isAdmin={isAdmin}
            closeCheckRef={editFormCloseRef}
          />
        </Modal>
      }
    </>
  );

}

const TabTeamSettingsReviews = ({ teamId, team, onLoading, isAdmin, changedByClientId }) => {
  return (
    <>
      <TeamSitesSelection
        teamId={teamId}
        team={team}
        onLoading={onLoading}
        type={'review'}
        isAdmin={isAdmin}
        changedByClientId={changedByClientId}
      />
    </>
  );
}

const TabTeamSettingsAffiliates = ({ teamId, team, onLoading, isAdmin, changedByClientId }) => {
  return (
    <>
      <TeamSitesSelection
        teamId={teamId}
        team={team}
        onLoading={onLoading}
        type={'affiliate'}
        isAdmin={isAdmin}
        changedByClientId={changedByClientId}
      />
    </>
  );
}

const TabTeamSettingsBookings = ({ teamId, team, onLoading, isAdmin, changedByClientId }) => {
  return (
    <>
      <TeamSitesSelection
        teamId={teamId}
        team={team}
        onLoading={onLoading}
        type={'book'}
        isAdmin={isAdmin}
        changedByClientId={changedByClientId}
      />
    </>
  );
}

const TabTeamSettingsLoyalties = ({ teamId, team, onLoading, isAdmin, changedByClientId }) => {
  return (
    <>
      <TeamSitesSelection
        teamId={teamId}
        team={team}
        onLoading={onLoading}
        type={'loyalty'}
        isAdmin={isAdmin}
        changedByClientId={changedByClientId}
      />
    </>
  );
}

const TabTeamSettingsSocials = ({ teamId, team, onLoading, isAdmin, changedByClientId }) => {
  return (
    <>
      <TeamSitesSelection
        teamId={teamId}
        team={team}
        onLoading={onLoading}
        type={'social'}
        isAdmin={isAdmin}
        changedByClientId={changedByClientId}
      />
    </>
  );
}

// const allTabs = [
//   { id: 'settings', tab: 'Permissions', component: TabMemberSettings, color: null, },
//   { id: 'image', tab: 'Branding', component: TabTeamSettingsImage, color: null, },
//   { id: 'name', tab: 'Name', component: TabTeamSettingsName, color: null, },
//   { id: 'values', tab: 'Values', component: TeamEditorValues, color: null, },
//   { id: 'percents', tab: 'Percentages', component: TeamEditorPercents, color: null, },
//   { id: 'review', tab: 'Reviews', component: TabTeamSettingsReviews, color: null, },
//   { id: 'shop', tab: 'Affilates', component: TabTeamSettingsAffiliates, color: null, },
//   { id: 'book', tab: 'Booking Apps', component: TabTeamSettingsBookings, color: null, },
// ];


const TeamSettingsBody = ({ teamId, locStrings, isAdmin, changedByClientId }) => {
  const outerTabId = `team-settings-tab-${teamId}`;

  const team = useTeamDetails(teamId);
  const defaultTag = useTagDetails(team ? team.defaultTagId : null);
  const [fgColor, setFgColor] = useState('text-white');
  const [fgMutedColor, setFgMutedColor] = useState('lightgrey');
  const [title, setTitle] = useState('...');
  const [tabPosition, setTabPosition] = useState('top');
  const [message, setMessage] = useState(null);
  
  // Get initial tab - check URL hash first, then sessionStorage, then default
  const getInitialTab = () => {
    // First check URL hash
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      // Support format like "general-name" where "general" is outer tab and "name" is inner tab
      const parts = hash.split('-');
      const outerTab = parts[0];
      if (outerTab && ['general', 'preset', 'actions'].includes(outerTab)) {
        return outerTab;
      }
      // Fallback to simple hash format
      if (['general', 'preset', 'actions'].includes(hash)) {
        return hash;
      }
    }
    // Then check sessionStorage (with error handling for invalid JSON)
    try {
      const stored = sessionStorage.getItem(outerTabId);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (['general', 'preset', 'actions'].includes(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      // If sessionStorage has invalid JSON, clear it and use default
      sessionStorage.removeItem(outerTabId);
    }
    return 'general';
  };
  
  // Get initial inner tab from URL hash
  const getInitialInnerTab = () => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const parts = hash.split('-');
      if (parts.length > 1) {
        return parts[1]; // Return the inner tab ID
      }
    }
    return null;
  };
  
  // Outer tab: must not use useSessionStorage alone — it prefers storage over default and ignores URL hash.
  // getInitialTab() already orders hash → sessionStorage → 'general'.
  const [activeTab, setActiveTab] = useState(() => getInitialTab());
  const [activeInnerTab, setActiveInnerTab] = useState(() => getInitialInnerTab());

  useEffect(() => {
    try {
      sessionStorage.setItem(outerTabId, JSON.stringify(activeTab));
    } catch (e) {
      // ignore quota / private mode
    }
  }, [outerTabId, activeTab]);
  const [actionsPreviewMode, setActionsPreviewMode] = useState('page');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Fetch all sites for the preview
  const [allTemplateSites] = useSitesByType({ status: 'live' });
  const [allTeamSites] = useTeamSitesByType({ teamId });

  const getMergedSites = () => {
    if (!allTemplateSites || !allTeamSites) return [];
    return allTemplateSites.map(template => {
      const teamOverride = allTeamSites.find(ts => ts.siteId === template.id && ts.status !== 'deactivated');
      if (!teamOverride) return null; // Only show sites that have been interacted with and are not deactivated
      return {
        ...template,
        ...teamOverride,
        templateButton: template.button,
        templateLink: template.link,
        templateColor: template.rgbColor,
        templateIsApp: !!template.isApp,
        templateTitle: template.title,
        templateDescription: template.description,
      };
    }).filter(Boolean);
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    teamName, teamId: id,
    topLogo,
    defaultTagId,
    values: teamValues,
    percents: teamPercents,
    defaultCurrency,
  } = team || {};

  // Use the Default Tag settings if the team settings are not set
  if (team && defaultTag) {
    const { values, percents } = defaultTag || {};    
    team.values = teamValues || values || [];
    team.percents = teamPercents || percents || [];
  }

  let { rgbColor } = team || {};
  rgbColor = rgbColor || '#FF0098';

  const { product } = defaultTag || {};
  const productSwitcher = new ProductSwitcher({ locStrings });
  const productMap = productSwitcher.getProductMap(product);
  const action = productMap.upper;

  const generalTabs = [
    { id: 'settings', tab: 'Permissions', component: TabMemberSettings, color: null, icon: <AuditOutlined /> },
    { id: 'image', tab: 'Branding', component: TabTeamSettingsImage, color: null,  icon: <ContactsOutlined /> },
    { id: 'name', tab: 'Name', component: TabTeamSettingsName, color: null, icon: <EditOutlined /> },
    { id: 'location', tab: 'Location', component: TabTeamLocationContact, color: null, icon: <GlobalOutlined /> },
  ];
  
  const presetAmountsTabs = [
    { id: 'values', tab: 'Values', component: TeamEditorValues, color: null, icon: <DollarCircleOutlined /> },
    { id: 'percents', tab: 'Percentages', component: TeamEditorPercents, color: null, icon: <PercentageOutlined /> },
    { id: 'payment', tab: 'Payment Details', component: TabPaymentDetailsSettings, color: null, icon: <CalculatorOutlined /> },
  ];
  
  // Visible to every manager since the Request Access gate (2026-09-29):
  // ungated teams see the request card, granted teams the settings form —
  // the component gates itself, so the tab no longer needs strike-admin.
  // The currency gate stands (Pay+Tip is EUR/GBP).
  const isPayAndTipAllowed = (defaultCurrency === 'EUR' || defaultCurrency === 'GBP');

  if (isPayAndTipAllowed) {
    presetAmountsTabs.push({ id: 'payandtip', tab: 'Pay + Tip', component: TabPayAndTipSettings, color: null, icon: <DollarOutlined /> });
  }
  
  const actionsTabs = [
    { id: 'review', tab: 'Reviews', component: TabTeamSettingsReviews, color: null, icon: <StarOutlined />},
    { id: 'book', tab: 'Booking', component: TabTeamSettingsBookings, color: null, icon: <CalendarOutlined /> },
    { id: 'shop', tab: 'Shopping', component: TabTeamSettingsAffiliates, color: null, icon: <ShoppingCartOutlined /> },
    { id: 'loyalty', tab: 'Loyalty', component: TabTeamSettingsLoyalties, color: null, icon: <CheckCircleOutlined /> },
    { id: 'social', tab: 'Social', component: TabTeamSettingsSocials, color: null, icon: <ContactsOutlined /> },
  ];
  
  const tabOfTabs = [
    { id: 'general', tab: 'Team', tabs: generalTabs, color: null, icon: <SettingTwoTone /> },
    { id: 'preset', tab: `${action}`, tabs: presetAmountsTabs, color: null, icon:  <AppstoreTwoTone /> },
    { id: 'actions', tab: `${action} success`, tabs: actionsTabs, color: null, icon: <CheckCircleTwoTone /> },
  ];

  // Deep links / in-app hash changes (hash wins over stale session when user navigates)
  useEffect(() => {
    const applyHashToTabs = () => {
      const hash = window.location.hash.replace('#', '');
      if (!hash) return;
      const parts = hash.split('-');
      const outerTab = parts[0];
      const innerTab = parts.length > 1 ? parts[1] : null;

      if (outerTab && ['general', 'preset', 'actions'].includes(outerTab)) {
        setActiveTab(outerTab);
      }
      if (innerTab) {
        setActiveInnerTab(innerTab);
      }
    };

    applyHashToTabs();
    window.addEventListener('hashchange', applyHashToTabs);
    return () => window.removeEventListener('hashchange', applyHashToTabs);
  }, []);

  useEffect(() => {
    const mode = getContrastWithRGBColor(rgbColor);
    const fgMutedColor = mode === 'light' ? 'grey' : 'lightgrey';
    setFgMutedColor(fgMutedColor);
    const textColor = colorForBackgroundColor(rgbColor);
    setFgColor(`text-${textColor}`);
    if (product) {
      const ps = new ProductSwitcher({ locStrings });
      const pm = ps.getProductMap(product);
      const lower = pm.lower;
      const title = `Leave a ${lower} for`
      setTitle(title);
    }
  }, [rgbColor, product]);

  const handleOnLoading = ({ message }) => {
    setMessage(message);

    if (message) {
      antdMessage.open({
        icon: <EditFilled />,
        content: message,
        duration: 2, // seconds
      });
    }
  }

  const tipSuccessSettingsInfo = (
    <Alert
      message="Tip Thank You Page Setup"
      description="Add reviews, rebooking, shopping, loyalty, and social links that guests see after they leave a tip. These links are also included in tip confirmation emails."
      type="info"
      showIcon
      style={{ marginBottom: '16px', borderRadius: '8px' }}
    />
  );

  const renderInnerTabs = (tabs, forceAccordion = false) => {
    // Use activeInnerTab if set and valid, otherwise default to first tab
    const isValidTab = activeInnerTab && tabs.find(t => t.id === activeInnerTab);
    const defaultKey = isValidTab ? activeInnerTab : tabs[0].id;
    
    if (isMobile || forceAccordion) {
      return (
        <Collapse
          accordion
          activeKey={defaultKey}
          onChange={(key) => setActiveInnerTab(Array.isArray(key) ? key[0] : key)}
          expandIconPosition="right"
          style={{ border: 'none', background: 'white' }}
        >
          {tabs.map(({ id, tab, component: TabComponent, color, icon }) => (
            <Panel 
              header={
                <span className="h4" style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                  {icon}<span className="mx-2">{tab}</span>
                </span>
              } 
              key={`${id}`}
              style={{ borderBottom: '1px solid #f0f0f0' }}
            >
              <div className='p-0 m-0 w-100' style={{ backgroundColor: color }}>
                <TabComponent teamId={teamId} team={team} defaultTag={defaultTag} onLoading={handleOnLoading} isAdmin={isAdmin} changedByClientId={changedByClientId} />
              </div>
            </Panel>
          ))}
        </Collapse>
      );
    }

    return (
      <Tabs
        type='line'
        size='small'
        centered
        tabPosition={'top'}
        style={{ color: 'black', margin: '0px', padding: '0px' }}
        tabBarStyle={{ padding: '0px' }}
        animated={false}
        activeKey={defaultKey}
        onChange={(key) => setActiveInnerTab(key)}
      >
        {tabs.map(({ id, tab, component: TabComponent, color, icon }) => (
          <TabPane
            tab={
            <span>
              {icon}
              {`${tab}`}
            </span>
            }
            key={`${id}`} style={{ padding: '0px', minHeight: '428px' }}
          >
            <div className='p-0 m-0' style={{ backgroundColor: color }}>
              <TabComponent teamId={teamId} team={team} defaultTag={defaultTag} onLoading={handleOnLoading} isAdmin={isAdmin} changedByClientId={changedByClientId} />
            </div>
          </TabPane>
        ))}
      </Tabs>
    );
  }

  return (
    <div className="team-screen" style={{ background: null }} >
      {!team && (
        <div className="container-fluid" style={{ padding: '64px 24px', textAlign: 'center' }}>
          <Spin size="large" tip="Loading team…" />
        </div>
      )}
      {team && !defaultTag && (
        <div className="container-fluid">
          <PageHeader
            ghost={false}
            onBack={() => window.history.back()}
            title={team.teamName}
            subTitle="Team settings"
            avatar={team.topLogo ? { src: team.topLogo, shape: 'square' } : null}
            style={{ background: 'white', padding: '16px 24px' }}
          />
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <Spin size="large" tip="Loading team tag & settings…" />
          </div>
        </div>
      )}
      {team && defaultTag &&
        <div className="container-fluid">
          <PageHeader
            ghost={false}
            onBack={() => window.history.back()}
            title={teamName}
            subTitle="Team settings"
            avatar={topLogo ? { src: topLogo, shape: 'square' } : null}
            style={{ background: 'white', padding: '16px 24px' }}
          />

          {message && (
            <Alert
              message={message}
              type="info"
              showIcon
              closable
              onClose={() => setMessage(null)}
              style={{ margin: '0 24px 16px' }}
            />
          )}

          <div className='p-0 m-0 w-100' style={{ background: 'white' }}>
            <Tabs
              type={isMobile ? 'line' : 'card'}
              size={isMobile ? 'middle' : 'small'}
              centered
              tabPosition={tabPosition}
              style={{ color: 'black', margin: '0px', padding: '0px' }}
              tabBarStyle={{ 
                padding: '0px', 
                ...(isMobile ? { marginBottom: '0px' } : {})
              }}
              animated={false}
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key)}
            >
              {
                tabOfTabs.map(({ id, tab, tabs, color, icon }) => (
                  <TabPane
                    tab={
                      <span>
                        {icon}
                        {`${tab}`}
                      </span>
                      }
                    key={`${id}`} style={{ padding: '0px', minHeight: '428px' }}
                  >
                    <div className='p-0 m-0' style={{ backgroundColor: color }}>
                      {/* <TabComponent teamId={teamId} team={team} defaultTag={defaultTag} onLoading={handleOnLoading} /> */}
                      {id === 'actions' && !isMobile ? (
                        <div className="container-fluid py-4">
                          <Row gutter={[24, 24]}>
                            <Col xs={24} lg={15}>
                              {tipSuccessSettingsInfo}
                              {renderInnerTabs(tabs, true)}
                            </Col>
                            <Col xs={24} lg={9}>
                              <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                                <Radio.Group
                                  size="small"
                                  value={actionsPreviewMode}
                                  onChange={(e) => setActionsPreviewMode(e.target.value)}
                                  buttonStyle="solid"
                                >
                                  <Radio.Button value="page">Thank You Page</Radio.Button>
                                  <Radio.Button value="email">Thank You Email</Radio.Button>
                                </Radio.Group>
                                <div style={{
                                  marginTop: '8px',
                                  fontSize: '11px',
                                  color: '#8c8c8c',
                                  letterSpacing: '0.2px',
                                }}
                                >
                                  This is a preview only. The actual pages and emails may look different.
                                </div>
                              </div>
                              {actionsPreviewMode === 'email' ? (
                                <ThankYouEmailPreview
                                  sites={getMergedSites()}
                                  team={team}
                                />
                              ) : (
                              <SuccessPagePreview 
                                sites={getMergedSites()} 
                                team={team}
                              />
                              )}
                            </Col>
                          </Row>
                        </div>
                      ) : id === 'preset' && !isMobile ? (
                        <div className="container-fluid py-4">
                          <Row gutter={[24, 24]}>
                            <Col xs={24} lg={15}>
                              {renderInnerTabs(tabs)}
                            </Col>
                            <Col xs={24} lg={9}>
                              <TipPagePreview team={team} />
                            </Col>
                          </Row>
                        </div>
                      ) : id === 'general' && !isMobile ? (
                        <div className="container-fluid py-4">
                          <Row gutter={[24, 24]}>
                            <Col xs={24} lg={15}>
                              {renderInnerTabs(tabs)}
                            </Col>
                            <Col xs={24} lg={9}>
                              <TeamPagePreview team={team} teamId={teamId} />
                            </Col>
                          </Row>
                        </div>
                      ) : (
                        <>
                          {id === 'actions' && (
                            <div className="container-fluid py-3">
                              {tipSuccessSettingsInfo}
                            </div>
                          )}
                          {renderInnerTabs(tabs, id === 'actions')}
                        </>
                      )}
                    </div>
                  </TabPane>
                ))
              }
            </Tabs>
          </div>
        </div>
      }
    </div>
  )
}

export default TeamSettingsBody;